// contexts/PanelContext.js
import React, { createContext, useContext, useState } from 'react';

// Define all possible panel components
export const PANEL_COMPONENTS = {
  MAIN_PANEL: {
    NOTE_VIEW: 'noteView',
    STICKY_NOTES: 'stickyNotesMain',
    AI_VIEW: 'aiView',
  },
  RIGHT_PANEL: {
    NOTE_VIEW: 'noteView',
    STICKY_NOTES: 'stickyNotes',
    THREADS: 'threads',
    IMAGE_GALLERY: 'imageGallery'
  },
  LEFT_PANEL: {
    TREE_VIEW: 'treeView',
    CATEGORY_VIEW: 'categoryView'
  }
};

const PanelContext = createContext();

export function PanelProvider({ children }) {
  // Track current content in each panel
  // const [rightPanelContent, setRightPanelContent] = useState(PANEL_COMPONENTS.RIGHT_PANEL.STICKY_NOTES);
  const [rightPanelContent, setRightPanelContent] = useState(PANEL_COMPONENTS.RIGHT_PANEL.NOTE_VIEW);
  // const [mainPanelContent, setMainPanelContent] = useState(PANEL_COMPONENTS.MAIN_PANEL.NOTE_VIEW);
  const [mainPanelContent, setMainPanelContent] = useState(PANEL_COMPONENTS.MAIN_PANEL.STICKY_NOTES);
  const [leftPanelContent, setLeftPanelContent] = useState(PANEL_COMPONENTS.LEFT_PANEL.TREE_VIEW);
  
  // Panel visibility states
  const [isRightPanelVisible, setIsRightPanelVisible] = useState(false);
  // const [isLeftPanelVisible, setIsLeftPanelVisible] = useState(true);
  const [isLeftPanelVisible, setIsLeftPanelVisible] = useState(true);
  
  // Track swapped state for right panel
  const [preSwapRightContent, setPreSwapRightContent] = useState(PANEL_COMPONENTS.RIGHT_PANEL.STICKY_NOTES);
  // const [isSwapped, setIsSwapped] = useState(false);
  const [isSwapped, setIsSwapped] = useState(true);

  // Toggle functions for panel visibility
  const toggleRightPanel = () => {
    setIsRightPanelVisible(prev => !prev);
  };

  const toggleLeftPanel = () => {
    setIsLeftPanelVisible(prev => !prev);
  };

  // Enhanced function to change right panel content that maintains history
  const changeRightPanelContent = (newContent) => {
    // If we're not in a swapped state, remember this content
    if (!isSwapped) {
      setPreSwapRightContent(newContent);
    }
    setRightPanelContent(newContent);
  };

  const swapPanels = () => {
    console.log('Current state before swap:', {
      rightContent: rightPanelContent,
      mainContent: mainPanelContent,
      isSwapped: isSwapped
    });
  
    if (isSwapped) {
      // When returning from swapped state:
      setMainPanelContent(PANEL_COMPONENTS.MAIN_PANEL.NOTE_VIEW);
      setRightPanelContent(preSwapRightContent);
      setIsSwapped(false);
    } else {
      // When performing initial swap, we need to:
      // 1. Save what's currently in right panel
      const currentRightContent = rightPanelContent;
      setPreSwapRightContent(currentRightContent);
  
      // 2. Handle the actual swap
      if (currentRightContent === PANEL_COMPONENTS.RIGHT_PANEL.THREADS) {
        // When threads are in right panel:
        // - Move threads to main panel
        setMainPanelContent(PANEL_COMPONENTS.RIGHT_PANEL.THREADS);
        // - Move NoteView to right panel
        setRightPanelContent(PANEL_COMPONENTS.RIGHT_PANEL.NOTE_VIEW);
      } else if (currentRightContent === PANEL_COMPONENTS.RIGHT_PANEL.STICKY_NOTES) {
        // Special case for sticky notes
        setMainPanelContent(PANEL_COMPONENTS.MAIN_PANEL.STICKY_NOTES);
        setRightPanelContent(PANEL_COMPONENTS.RIGHT_PANEL.NOTE_VIEW);
      }
      
      setIsSwapped(true);
    }
  };

  const value = {
    // Panel content states
    rightPanelContent,
    changeRightPanelContent,
    mainPanelContent,
    setMainPanelContent,
    leftPanelContent,
    setLeftPanelContent,
    
    // Panel visibility states
    isRightPanelVisible,
    setIsRightPanelVisible,
    toggleRightPanel,
    isLeftPanelVisible,
    setIsLeftPanelVisible,
    toggleLeftPanel,
    
    // Panel swap functionality
    swapPanels,
    isSwapped,
    
    // Constants
    PANEL_COMPONENTS
  };

  return (
    <PanelContext.Provider value={value}>
      {children}
    </PanelContext.Provider>
  );
}

export function usePanelContext() {
  const context = useContext(PanelContext);
  if (!context) {
    throw new Error('usePanelContext must be used within a PanelProvider');
  }
  return context;
}